// Logo component
import React,{useState} from "react";
import LogoImage from "../../assets/images/Final.png";
import { Eye, EyeOff, Info } from "lucide-react";
import LabelInput from "../../components/common/forms/Labelinput"; // Reusable input component
import { useAuth } from "../../context/AuthContext"; // Auth context
import { API_CONFIG, getHeaders } from "../../config/api"; // API config (for future real login)
import { useNavigate } from "react-router-dom";



const MyLogo =() => {
  return (
    <div className="flex items-center justify-center">
      <img 
        src={LogoImage} 
        alt="Company Logo" 
        className="object-cover h-16 w-16"
      />
    </div>
  );
}


export default function Login() {
  const { login } = useAuth(); // Auth context function to store user & token
  const navigate = useNavigate(); // For redirecting after login

  const [hide, setHide] = useState(true); // Toggle password visibility
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  /* =============================
      Future: Real API login (commented out)
      ============================= */

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        `${API_CONFIG.BASE_URL}${API_CONFIG.ENDPOINTS.AUTH.LOGIN}`,
        {
          method: "POST",
          headers: getHeaders(false),
          body: JSON.stringify({ email, password }),
        }
      );


      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.message || "Invalid credentials");
      }

      const data = await response.json();

      login({
        user: {
          username: data.data.username,
          email: data.data.email,
        },
        accessToken: data.data.access_token,
        refreshToken: data.data.refresh_token
      });


      // redirect to dashboard
      navigate("/dashboard");
    } catch (err) {
      console.error("Login error:", err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="h-dvh flex items-center justify-center bg-gray-50 relative">
      {/* Top-left logo */}
      <div className="absolute top-6 left-6">
        <MyLogo />
      </div>

      <div className="login-card space-y-6 p-6">
        {/* Header */}
        <div className="text-center">
          <h1
            className="font-bold tracking-widest text-gray-900"
            style={{ fontFamily: "'Lora', serif", fontSize: "var(--text-3xl)" }}
          >
            Welcome Back!👋
          </h1>

          <h2 className="font-medium text-gray-900" style={{ fontSize: "var(--text-xl)" }} >
            Sign in to your account
          </h2>
          <p className="mt-2 text-gray-600" style={{ fontSize: "var(--text-base)" }}>
            Enter your credentials to access the dashboard
          </p>
        </div>

        {/* Info Box */}
        <div className="rounded-md bg-sidebarHover p-4">
          <div className="flex items-start">
            <Info className="h-5 w-5 text-primary mt-0.5" />
            <div className="ml-3">
              <p className="text-primary" style={{ fontSize: "var(--text-sm)" }} >
                Use <span className="font-semibold">your email</span> and <span className="font-semibold">password</span> to log in.
                <br />Make sure your credentials are correct before continuing.
              </p>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="rounded-md bg-red-50 p-4">
            <div className="flex">
              <div className="ml-3">
                <p className="text-sm text-red-800">{error}</p>
              </div>
            </div>
          </div>
        )}

        {/* Form */}
        <form className="space-y-5" onSubmit={handleSubmit}>
          {/* Email input */}
          <LabelInput
            label="Email address"
            type="email"
            name="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            disabled={loading}
          />
          {/* Password input with toggle visibility */}
          <div className="relative">
            <LabelInput
              label="Password"
              type={hide ? "password" : "text"}
              name="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              disabled={loading}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-4 flex items-center"
              onClick={() => setHide(!hide)}
              disabled={loading}
            >
              {hide ? (
                <EyeOff className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              ) : (
                <Eye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              )}
            </button>
          </div>
          {/* Submit button */}
          <button
            type="submit"
            disabled={loading}
            className="w-full flex justify-center py-3 px-4 rounded-lg text-white hover:bg-black bg-colorButton disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? "Signing in..." : "Sign in"}
          </button>
        </form>
      </div>
    </div>
  );
}